import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import DashboardFooter from "./DashboardFooter";
import "../css/Staff.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaPlus,
  FaSync,
  FaSearch,
  FaTimes,
  FaFilter,
} from "react-icons/fa";

const TRADE_API_URL =
  "https://auxititpt.org/Backend/trade_api.php";

const TradeManagement = () => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTrade, setEditTrade] = useState(null);
  const [form, setForm] = useState({
    trade_name: "",
    duration: "",
    qualification: "",
    syllabus_url: "",
    approved_by: "",
  });
  const [viewTrade, setViewTrade] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDurations, setFilterDurations] = useState([]);
  const [filterQualifications, setFilterQualifications] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempDurations, setTempDurations] = useState([]);
  const [tempQualifications, setTempQualifications] = useState([]);

  useEffect(() => {
    fetchTrades();
  }, []);

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const res = await fetch(TRADE_API_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTrades(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch trades:", err);
      alert("Failed to load trades. Check console.");
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm({
      trade_name: "",
      duration: "",
      qualification: "",
      syllabus_url: "",
      approved_by: "",
    });
    setEditTrade(null);
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.trade_name || form.trade_name.trim() === "") {
      return alert("Trade name is required");
    }

    try {
      if (editTrade) {
        // PUT (update)
        const body = new URLSearchParams();
        body.append("trade_id", editTrade.trade_id);
        body.append("trade_name", form.trade_name);
        body.append("duration", form.duration || "");
        body.append("qualification", form.qualification || "");
        body.append("syllabus_url", form.syllabus_url || "");
        body.append("approved_by", form.approved_by || "");

        const res = await fetch(TRADE_API_URL, {
          method: "PUT",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: body.toString(),
        });
        const result = await res.json();
        if (res.ok && result.status === "success") {
          alert("Trade updated");
          fetchTrades();
          setShowForm(false);
        } else throw new Error(result.message || "Update failed");
      } else {
        // POST (add)
        const res = await fetch(TRADE_API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        const result = await res.json();
        if (res.ok && result.status === "success") {
          alert("Trade added");
          fetchTrades();
          setShowForm(false);
        } else throw new Error(result.message || "Add failed");
      }
    } catch (err) {
      console.error("Trade submit error:", err);
      alert("Error: " + err.message);
    }
  };

  const handleEdit = (trade) => {
    setEditTrade(trade);
    setForm({
      trade_name: trade.trade_name || "",
      duration: trade.duration || "",
      qualification: trade.qualification || "",
      syllabus_url: trade.syllabus_url || "",
      approved_by: trade.approved_by || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (trade) => {
    if (!window.confirm(`Delete trade "${trade.trade_name}"?`)) return;
    try {
      const res = await fetch(`${TRADE_API_URL}?id=${trade.trade_id}`, {
        method: "DELETE",
      });
      const result = await res.json();
      if (res.ok && result.status === "success") {
        alert("Trade deleted");
        fetchTrades();
      } else throw new Error(result.message || "Delete failed");
    } catch (err) {
      console.error("Delete trade error:", err);
      alert("Error: " + err.message);
    }
  };

  const handleView = async (trade) => {
    try {
      const res = await fetch(`${TRADE_API_URL}?id=${trade.trade_id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setViewTrade(data);
    } catch (err) {
      console.error("View trade error:", err);
      alert("Failed to load trade details");
    }
  };

  // Unique lists for filters
  const uniqueDurations = Array.from(
    new Set(trades.map((t) => (t.duration || "").trim()).filter(Boolean))
  );
  const uniqueQualifications = Array.from(
    new Set(trades.map((t) => (t.qualification || "").trim()).filter(Boolean))
  );

  // Modal toggle helper
  const toggleTemp = (val, arr, setArr) => {
    if (arr.includes(val)) setArr(arr.filter((x) => x !== val));
    else setArr([...arr, val]);
  };

  const openFilterModal = () => {
    setTempDurations([...filterDurations]);
    setTempQualifications([...filterQualifications]);
    setShowFilterModal(true);
  };

  const applyFilters = () => {
    setFilterDurations([...tempDurations]);
    setFilterQualifications([...tempQualifications]);
    setShowFilterModal(false);
  };

  const clearModal = () => {
    setTempDurations([]);
    setTempQualifications([]);
  };

  const clearAllFilters = () => {
    setFilterDurations([]);
    setFilterQualifications([]);
    setSearchQuery("");
  };

  // Filtering logic
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredTrades = trades.filter((t) => {
    if (filterDurations.length > 0 && !filterDurations.includes((t.duration || "").trim()))
      return false;
    if (
      filterQualifications.length > 0 &&
      !filterQualifications.includes((t.qualification || "").trim())
    )
      return false;
    if (!normalizedQuery) return true;
    const fields = [
      t.trade_name || "",
      t.duration || "",
      t.qualification || "",
    ];
    return fields.some((f) => f.toLowerCase().includes(normalizedQuery));
  });

  const activeFilterCount = filterDurations.length + filterQualifications.length;

  return (
    <div className="trade-page">
      <div className="d-flex dashboard-container">
        <Sidebar />
        <div className="main-content flex-grow-1">
          <Header />
          <div className="container py-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3 className="fw-bold">Manage Trades</h3>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-primary me-2 d-flex align-items-center"
                  onClick={openFilterModal}
                >
                  <FaFilter className="me-2" /> Filters
                  {activeFilterCount > 0 && (
                    <span className="badge bg-danger ms-2">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <button
                  className="btn btn-primary me-2 d-flex align-items-center"
                  onClick={openAdd}
                >
                  <FaPlus className="me-2" /> Add Trade
                </button>
                <button
                  className="btn btn-outline-secondary d-flex align-items-center"
                  onClick={fetchTrades}
                >
                  <FaSync className="me-2" /> Refresh
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="card mb-3 p-3">
              <div className="row g-2 align-items-center">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by trade name, duration, or qualification..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setSearchQuery("")}
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={clearAllFilters}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Add/Edit Form */}
            {showForm && (
              <form className="card shadow mb-4 p-4" onSubmit={handleSubmit}>
                <h5 className="fw-bold mb-3 text-primary">
                  {editTrade ? "Edit Trade" : "Add New Trade"}
                </h5>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Trade Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="trade_name"
                      value={form.trade_name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Duration</label>
                    <input
                      type="text"
                      className="form-control"
                      name="duration"
                      value={form.duration}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Qualification</label>
                    <input
                      type="text"
                      className="form-control"
                      name="qualification"
                      value={form.qualification}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Approved By</label>
                    <input
                      type="text"
                      className="form-control"
                      name="approved_by"
                      value={form.approved_by}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Syllabus URL</label>
                    <input
                      type="url"
                      className="form-control"
                      name="syllabus_url"
                      value={form.syllabus_url}
                      onChange={handleChange}
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="mt-3 text-end">
                  <button type="submit" className="btn btn-success me-2">
                    {editTrade ? "Update" : "Save"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowForm(false);
                      setEditTrade(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Trades Table */}
            <div className="card shadow">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped table-bordered align-middle">
                    <thead className="table-dark text-center">
                      <tr>
                        <th>#</th>
                        <th>Trade Name</th>
                        <th>Duration</th>
                        <th>Qualification</th>
                        <th>Approved By</th>
                        <th>Syllabus</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : filteredTrades.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center text-muted py-3"
                          >
                            No trades found
                          </td>
                        </tr>
                      ) : (
                        filteredTrades.map((trade, idx) => (
                          <tr key={trade.trade_id}>
                            <td>{idx + 1}</td>
                            <td>{trade.trade_name}</td>
                            <td>{trade.duration || "—"}</td>
                            <td>{trade.qualification || "—"}</td>
                            <td>{trade.approved_by || "—"}</td>
                            <td>
                              {trade.syllabus_url ? (
                                <a
                                  href={trade.syllabus_url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-info me-1"
                                onClick={() => handleView(trade)}
                              >
                                <FaEye />
                              </button>
                              <button
                                className="btn btn-sm btn-warning me-1"
                                onClick={() => handleEdit(trade)}
                              >
                                <FaEdit />
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(trade)}
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* View Modal */}
            {viewTrade && (
              <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onClick={() => setViewTrade(null)}
              >
                <div
                  className="modal-dialog"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Trade Details</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setViewTrade(null)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <p>
                        <strong>Name:</strong> {viewTrade.trade_name}
                      </p>
                      <p>
                        <strong>Duration:</strong>{" "}
                        {viewTrade.duration || "—"}
                      </p>
                      <p>
                        <strong>Qualification:</strong>{" "}
                        {viewTrade.qualification || "—"}
                      </p>
                      <p>
                        <strong>Approved By:</strong>{" "}
                        {viewTrade.approved_by || "—"}
                      </p>
                      <p>
                        <strong>Syllabus:</strong>{" "}
                        {viewTrade.syllabus_url ? (
                          <a
                            href={viewTrade.syllabus_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open
                          </a>
                        ) : (
                          "—"
                        )}
                      </p>
                      <p>
                        <strong>Created:</strong>{" "}
                        {viewTrade.created_at || "—"}
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary"
                        onClick={() => setViewTrade(null)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Modal */}
            {showFilterModal && (
              <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onClick={() => setShowFilterModal(false)}
              >
                <div
                  className="modal-dialog modal-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Filters</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowFilterModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <h6>Duration</h6>
                          <div
                            style={{
                              maxHeight: 180,
                              overflowY: "auto",
                              border: "1px solid #eee",
                              padding: 8,
                              borderRadius: 4,
                            }}
                          >
                            {uniqueDurations.length === 0 ? (
                              <div className="text-muted">No durations</div>
                            ) : (
                              uniqueDurations.map((d) => (
                                <div key={d} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`dur_${d}`}
                                    checked={tempDurations.includes(d)}
                                    onChange={() =>
                                      toggleTemp(
                                        d,
                                        tempDurations,
                                        setTempDurations
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`dur_${d}`}
                                  >
                                    {d}
                                  </label>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <h6>Qualification</h6>
                          <div
                            style={{
                              maxHeight: 180,
                              overflowY: "auto",
                              border: "1px solid #eee",
                              padding: 8,
                              borderRadius: 4,
                            }}
                          >
                            {uniqueQualifications.length === 0 ? (
                              <div className="text-muted">No qualifications</div>
                            ) : (
                              uniqueQualifications.map((q) => (
                                <div key={q} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`qual_${q}`}
                                    checked={tempQualifications.includes(q)}
                                    onChange={() =>
                                      toggleTemp(
                                        q,
                                        tempQualifications,
                                        setTempQualifications
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`qual_${q}`}
                                  >
                                    {q}
                                  </label>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer d-flex justify-content-between">
                      <div>
                        <button
                          className="btn btn-outline-secondary me-2"
                          onClick={clearModal}
                        >
                          Clear Selection
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => {
                            clearAllFilters();
                            setShowFilterModal(false);
                          }}
                        >
                          Clear All Filters
                        </button>
                      </div>
                      <div>
                        <button
                          className="btn btn-secondary me-2"
                          onClick={() => setShowFilterModal(false)}
                        >
                          Cancel
                        </button>
                        <button className="btn btn-primary" onClick={applyFilters}>
                          Apply Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
          <DashboardFooter />
        </div>
      </div>
    </div>
  );
};

export default TradeManagement;